import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Shield,
  ArrowRight,
  TrendingUp,
  Building2
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const Dashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState({
    total_claims: 0,
    pending_survey: 0,
    proses_survey: 0,
    klaim_ditolak: 0,
    klaim_dijamin: 0
  });
  const [recentClaims, setRecentClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, claimsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-claims?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentClaims(claimsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_survey: { label: "Pending Survey", class: "badge-pending" },
      proses_survey: { label: "Proses Survey", class: "badge-process" },
      klaim_ditolak: { label: "Ditolak", class: "badge-rejected" },
      klaim_dijamin: { label: "Dijamin", class: "badge-approved" }
    };
    const config = statusConfig[status] || { label: status, class: "badge-pending" };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const statCards = [
    {
      title: "Total Klaim",
      value: stats.total_claims,
      icon: FileText,
      color: "text-slate-900",
      bgColor: "bg-slate-100"
    },
    {
      title: "Pending Survey",
      value: stats.pending_survey,
      icon: Clock,
      color: "text-amber-600",
      bgColor: "bg-amber-100"
    },
    {
      title: "Proses Survey",
      value: stats.proses_survey,
      icon: TrendingUp,
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      title: "Klaim Dijamin",
      value: stats.klaim_dijamin,
      icon: CheckCircle2,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    }
  ];

  const chartData = [
    { name: "Pending Survey", value: stats.pending_survey, color: "#F59E0B" },
    { name: "Proses Survey", value: stats.proses_survey, color: "#2563EB" },
    { name: "Ditolak", value: stats.klaim_ditolak, color: "#EF4444" },
    { name: "Dijamin", value: stats.klaim_dijamin, color: "#10B981" }
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Selamat datang kembali, {user?.name}</p>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid mb-8">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="stat-card card-hover" data-testid={`stat-card-${index}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-1">{card.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{card.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${card.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${card.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-grid">
          {/* Chart Section */}
          <div className="col-span-12 lg:col-span-5">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Distribusi Status Klaim
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [value, name]}
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value) => <span className="text-sm text-slate-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">Belum ada data klaim</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Claims */}
          <div className="col-span-12 lg:col-span-7">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Klaim Terbaru
                </CardTitle>
                <Link to="/claims">
                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" data-testid="view-all-claims-btn">
                    Lihat Semua <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {recentClaims.length > 0 ? (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>No. Polis</th>
                          <th>Perusahaan</th>
                          <th>Status</th>
                          <th>Tanggal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentClaims.map((claim) => (
                          <tr key={claim.claim_id} data-testid={`recent-claim-${claim.claim_id}`}>
                            <td className="font-medium">{claim.policy_number}</td>
                            <td>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                {claim.company_name}
                              </div>
                            </td>
                            <td>{getStatusBadge(claim.status)}</td>
                            <td className="text-slate-500">
                              {new Date(claim.created_at).toLocaleDateString('id-ID')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <FileText className="empty-state-icon" />
                    <p className="empty-state-title">Belum ada klaim</p>
                    <p className="empty-state-description">
                      {user?.role !== "guest" 
                        ? "Mulai buat klaim baru untuk melihat data di sini"
                        : "Belum ada data klaim yang tersedia"}
                    </p>
                    {user?.role !== "guest" && (
                      <Link to="/claims">
                        <Button className="btn-accent" data-testid="create-first-claim-btn">
                          Buat Klaim Pertama
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats for Rejected */}
          <div className="col-span-12 lg:col-span-4">
            <Card className="bg-red-50 border-red-100">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                    <XCircle className="w-7 h-7 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-600">Klaim Ditolak</p>
                    <p className="text-3xl font-bold text-red-700">{stats.klaim_ditolak}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Company Info */}
          <div className="col-span-12 lg:col-span-8">
            <Card className="bg-slate-900 text-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center">
                      <Shield className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">PT. Nayaka Integritas Solusi Indonesia</h3>
                      <p className="text-slate-400">Jasa Claim Investigator Terpercaya</p>
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm text-slate-400">Total Investigasi</p>
                    <p className="text-2xl font-bold">{stats.total_claims}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
